import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Search from '@mui/icons-material/Search';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  parseFilterCommand,
  rankFilterCategories,
} from '~components/Filters/filterCategorySearch.utils';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { StatusTabValue } from '~components/Filters/OrdersStatusFilter';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { DispatchURLParams } from '~constants/dispatch';
import { SelectedFilter } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import useFilterCategoryLabels from '~hooks/useFilterCategoryLabels';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import useOrdersDispatchFilters, {
  ordersDispatchFilterKeyBySelectedFilter,
} from '../../hooks/useOrdersDispatchFilters';
import {
  DUMMY_QUERY_FILTERS,
  OrderDispatchQuery,
  useCollaboratorsQuery,
  useCustomersQuery,
  useDepartmentsQuery,
  useDispatchNumbersQuery,
  useDriversQuery,
  useDropOffSitesQuery,
  useDummyQuery,
  useEquipmentOwnersQuery,
  useEquipmentQuery,
  useEquipmentTypesQuery,
  useForemenQuery,
  useMaterialsQuery,
  usePickUpSitesQuery,
  useProjectExternalIdsQuery,
  useProjectsQuery,
  useRequestersQuery,
  useSalespeopleQuery,
  useServiceClassesQuery,
  useVendorsQuery,
} from './orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';

const OMNI_BAR_MIN_WIDTH_IN_PX = 260;
// Grow into the toolbar's free space, capped so the bar stays a search field, not a banner.
const OMNI_BAR_MAX_WIDTH_IN_PX = 520;
const OMNI_POPOVER_MAX_HEIGHT_IN_PX = 420;
// Matches the mounted SearchBar so free-text behaviour is identical (min 3 chars).
const OMNI_MIN_SEARCH_LENGTH = 3;

// The filters dispatchers reach for most — surfaced on focus before any typing.
const FREQUENT_FILTERS: SelectedFilter[] = [
  SelectedFilter.CUSTOMERS,
  SelectedFilter.DRIVERS,
  SelectedFilter.JOB_STATES,
  SelectedFilter.MATERIALS,
  SelectedFilter.PROJECTS,
  SelectedFilter.ORDER_STATES_REFINED,
];

const NO_HIGHLIGHT = -1;

// Where the bar's text came from. A deep-linked order id is shown for context but must
// NOT be pushed to the store as a search (mirrors SearchBar's FilterSource.DEEP_LINK).
type FreeTextSource = 'input' | 'deep-link';

const OmniSearchBar = observer(() => {
  const { allEntitiesLabel, categoryLabel, noEntitiesLabel } = useFilterCategoryLabels();
  const theme = useTheme();
  const { userStore, ordersDispatchStore } = useStores();

  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get(DispatchURLParams.TAB) as StatusTabValue;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>();
  const [rootHighlight, setRootHighlight] = useState(NO_HIGHLIGHT);
  const inputRef = useRef<HTMLInputElement>();

  // Free-text order search — mirrors SearchBar (filters.search + debounce + min length).
  const [freeText, setFreeText] = useState<{ value: string; source: FreeTextSource }>({
    value: '',
    source: 'input',
  });

  const {
    availableOptions,
    updateAvailableOptions,
    updateAppliedFilterChipsCollectedData,
  } = useOrdersDispatchFilters();

  const quickDepartmentFiltersFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.quickDepartmentFilters,
  });

  const aiReviewLoadsEnabled = userStore.userCompany.aiReviewLoads;
  const defaultDigitalTicketSettingEnabled = userStore.companySettings.isEnabled(
    'show_default_digital_ticket_view',
  );
  const companyId = getEffectiveUserCompanyId(userStore);
  const companyLegalName = userStore.currentCompanies?.[0]?.legalName ?? '';
  const noneOptionFilters = [SelectedFilter.DEPARTMENTS].concat(
    companyLegalName ? [SelectedFilter.VENDORS] : [],
  );

  // Option-level typeahead inside a drilled filter (distinct from free-text order search).
  const [searchQuery, setSearchQuery] = useState<{ input: string; debounced: string }>({
    input: '',
    debounced: '',
  });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const applyFreeText = useRef(
    debounce((value: string) => {
      ordersDispatchStore.setFilters({ search: value }, true);
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const aiReviewStatusesQuery = useDummyQuery(availableOptions.aiReviewStatuses);
  const collaboratorsQuery = useCollaboratorsQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const customersQuery = useCustomersQuery(companyId, ordersDispatchStore.filters);
  const departmentsQuery = useDepartmentsQuery(companyId, ordersDispatchStore.filters);
  const dispatchNumbersQuery = useDispatchNumbersQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const driversQuery = useDriversQuery(companyId, ordersDispatchStore.filters);
  const dropOffSitesQuery = useDropOffSitesQuery(companyId, ordersDispatchStore.filters);
  const equipmentOwnersQuery = useEquipmentOwnersQuery(companyId, {
    id: companyId,
    name: companyLegalName,
  });
  const equipmentQuery = useEquipmentQuery(companyId, ordersDispatchStore.filters);
  const equipmentTypesQuery = useEquipmentTypesQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const foremenQuery = useForemenQuery(companyId, ordersDispatchStore.filters);
  const dispatchStatusesQuery = useDummyQuery(availableOptions.dispatchStatuses);
  const jobStatesQuery = useDummyQuery(availableOptions.jobStates);
  const materialsQuery = useMaterialsQuery(companyId, ordersDispatchStore.filters);
  const pickUpSitesQuery = usePickUpSitesQuery(companyId, ordersDispatchStore.filters);
  const projectExternalIdsQuery = useProjectExternalIdsQuery(companyId);
  const projectsQuery = useProjectsQuery(companyId, ordersDispatchStore.filters);
  const refinedOrderStatesQuery = useDummyQuery(availableOptions.refinedOrderStates);
  const requestersQuery = useRequestersQuery(companyId, ordersDispatchStore.filters);
  const salespeopleQuery = useSalespeopleQuery(companyId, ordersDispatchStore.filters);
  const serviceClassesQuery = useServiceClassesQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const siteTypesQuery = useDummyQuery(availableOptions.siteTypes);
  const prevailingWageQuery = useDummyQuery(availableOptions.prevailingWage);
  const ticketPotentialDuplicateQuery = useDummyQuery(availableOptions.duplicateTickets);
  const ticketTypesQuery = useDummyQuery(availableOptions.ticketTypes);
  const vendorsQuery = useVendorsQuery(companyId, ordersDispatchStore.filters);

  const [excludedFilters] = useMemo(() => {
    const excluded: SelectedFilter[] = [];

    if (currentTab === StatusTabValue.PENDING) {
      excluded.push(SelectedFilter.ORDER_STATES_REFINED);
    }

    if (!aiReviewLoadsEnabled) {
      excluded.push(SelectedFilter.AI_REVIEW_STATUSES);
    }

    if (quickDepartmentFiltersFeatureFlagEnabled) {
      excluded.push(SelectedFilter.DEPARTMENTS);
    }

    if (!defaultDigitalTicketSettingEnabled) {
      excluded.push(SelectedFilter.TICKET_TYPES);
    }

    if (!ordersDispatchStore.isFetchingOrdersForASingleDay) {
      excluded.push(SelectedFilter.DISPATCH_STATUSES);
    }

    return [excluded];
  }, [
    aiReviewLoadsEnabled,
    currentTab,
    defaultDigitalTicketSettingEnabled,
    ordersDispatchStore.isFetchingOrdersForASingleDay,
    quickDepartmentFiltersFeatureFlagEnabled,
  ]);

  const categoriesWithLabels = useMemo(
    () =>
      Object.values(SelectedFilter)
        .filter((filter) => !excludedFilters.includes(filter))
        .map((filter) => ({
          filter,
          label: categoryLabel(filter),
        })),
    [categoryLabel, excludedFilters],
  );

  const activeFilters = useMemo(() => {
    const withSelections = new Set<SelectedFilter>();

    categoriesWithLabels.forEach(({ filter }) => {
      const key = ordersDispatchFilterKeyBySelectedFilter[filter];

      if ((ordersDispatchStore.filters[key] ?? []).length > 0) {
        withSelections.add(filter);
      }
    });

    return withSelections;
  }, [categoriesWithLabels, ordersDispatchStore.filters]);

  const isSearching = freeText.value.trim().length > 0;

  // Empty focus shows Frequent (quick access) then All (nothing is hidden); typing shows a
  // single relevance-ranked list.
  const rootSections = useMemo(() => {
    if (isSearching) {
      return [
        {
          key: 'ranked',
          label: null,
          categories: rankFilterCategories(categoriesWithLabels, freeText.value.trim()),
        },
      ];
    }

    const byFilter = new Map(categoriesWithLabels.map((entry) => [entry.filter, entry]));
    const frequent = FREQUENT_FILTERS.map((filter) => byFilter.get(filter)).filter(
      (entry): entry is { filter: SelectedFilter; label: string } => Boolean(entry),
    );
    const frequentSet = new Set<SelectedFilter>(FREQUENT_FILTERS);
    const rest = categoriesWithLabels.filter((entry) => !frequentSet.has(entry.filter));

    return [
      {
        key: 'frequent',
        label: t('dispatch.dispatch_v2.filters.frequent_filters'),
        categories: frequent,
      },
      {
        key: 'all',
        label: t('dispatch.dispatch_v2.filters.all_filters'),
        categories: rest,
      },
    ];
  }, [isSearching, freeText.value, categoriesWithLabels]);

  const visibleCategories = useMemo(
    () => rootSections.flatMap((section) => section.categories),
    [rootSections],
  );

  const highlightIndexByFilter = useMemo(
    () => new Map(visibleCategories.map((category, index) => [category.filter, index])),
    [visibleCategories],
  );

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const queries: Record<SelectedFilter, OrderDispatchQuery> = {
      [SelectedFilter.AI_REVIEW_STATUSES]: aiReviewStatusesQuery,
      [SelectedFilter.COLLABORATORS]: collaboratorsQuery,
      [SelectedFilter.CUSTOMERS]: customersQuery,
      [SelectedFilter.DEPARTMENTS]: departmentsQuery,
      [SelectedFilter.DISPATCH_NUMBERS]: dispatchNumbersQuery,
      [SelectedFilter.DISPATCH_STATUSES]: dispatchStatusesQuery,
      [SelectedFilter.DRIVERS]: driversQuery,
      [SelectedFilter.DROP_OFF_SITES]: dropOffSitesQuery,
      [SelectedFilter.EQUIPMENT]: equipmentQuery,
      [SelectedFilter.EQUIPMENT_OWNERS]: equipmentOwnersQuery,
      [SelectedFilter.EQUIPMENT_TYPES]: equipmentTypesQuery,
      [SelectedFilter.FOREMEN]: foremenQuery,
      [SelectedFilter.JOB_STATES]: jobStatesQuery,
      [SelectedFilter.MATERIALS]: materialsQuery,
      [SelectedFilter.ORDER_STATES_REFINED]: refinedOrderStatesQuery,
      [SelectedFilter.PICK_UP_SITES]: pickUpSitesQuery,
      [SelectedFilter.PREVAILING_WAGE]: prevailingWageQuery,
      [SelectedFilter.PROJECTS]: projectsQuery,
      [SelectedFilter.PROJECTS_EXTERNAL_IDS]: projectExternalIdsQuery,
      [SelectedFilter.REQUESTERS]: requestersQuery,
      [SelectedFilter.SALESPEOPLE]: salespeopleQuery,
      [SelectedFilter.SERVICE_CLASSES]: serviceClassesQuery,
      [SelectedFilter.SITE_TYPES]: siteTypesQuery,
      [SelectedFilter.DUPLICATE_TICKETS]: ticketPotentialDuplicateQuery,
      [SelectedFilter.TICKET_TYPES]: ticketTypesQuery,
      [SelectedFilter.VENDORS]: vendorsQuery,
    };

    return queries[selectedFilter];
  }, [
    selectedFilter,
    aiReviewStatusesQuery,
    collaboratorsQuery,
    customersQuery,
    departmentsQuery,
    dispatchNumbersQuery,
    dispatchStatusesQuery,
    driversQuery,
    dropOffSitesQuery,
    equipmentOwnersQuery,
    equipmentQuery,
    equipmentTypesQuery,
    foremenQuery,
    jobStatesQuery,
    materialsQuery,
    pickUpSitesQuery,
    prevailingWageQuery,
    projectExternalIdsQuery,
    projectsQuery,
    requestersQuery,
    salespeopleQuery,
    serviceClassesQuery,
    siteTypesQuery,
    ticketPotentialDuplicateQuery,
    ticketTypesQuery,
    vendorsQuery,
  ]);

  const isDummyQuery = useMemo(() => {
    return selectedFilter && DUMMY_QUERY_FILTERS.includes(selectedFilter);
  }, [selectedFilter]);

  const searchValueChangeCallback = useMemo(() => {
    if (isDummyQuery) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [isDummyQuery]);

  const fetchMoreCallback = useMemo(
    () => activeQuery?.fetchMore,
    [activeQuery?.fetchMore],
  );

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = ordersDispatchFilterKeyBySelectedFilter[selectedFilter];

    return ordersDispatchStore.filters[key] ?? [];
  }, [ordersDispatchStore.filters, selectedFilter]);

  const handleSelectedOptionsChange = useCallback(
    (options: string[]) => {
      if (selectedFilter) {
        const key = ordersDispatchFilterKeyBySelectedFilter[selectedFilter];

        ordersDispatchStore.setFilters(
          { [key]: options.length > 0 ? options : undefined },
          true,
        );
      }
    },
    // setFilters is a stable MobX action; never depend on the store object.
    [selectedFilter],
  );

  const updateAvailableOptionsAndFilterChips = useCallback(
    (key: SelectedFilter, data: FilterMenuOption[]) => {
      updateAvailableOptions(key, data);
      updateAppliedFilterChipsCollectedData(key, data);
    },
    [updateAvailableOptions, updateAppliedFilterChipsCollectedData],
  );

  const getNoneOptionLabel = (filter: SelectedFilter) => {
    if (noneOptionFilters.includes(filter)) {
      if (filter === SelectedFilter.DEPARTMENTS) {
        return t('dispatch.dispatch_v2.filters.departments_filter_none_option');
      }

      if (filter === SelectedFilter.VENDORS && companyLegalName) {
        return companyLegalName;
      }

      return noEntitiesLabel(filter);
    }
  };

  const openDrill = useCallback((filter: SelectedFilter, optionSeed: string) => {
    // The bar text was used to find this filter, not to search orders — clear the
    // free-text search so a set filter isn't also constrained by a stale query.
    setFreeText({ value: '', source: 'input' });
    setSelectedFilter(filter);
    setSearchQuery({ input: optionSeed, debounced: optionSeed });
    setIsOpen(true);
  }, []);

  const handleBackToCategories = useCallback(() => {
    setSelectedFilter(undefined);
    setSearchQuery({ input: '', debounced: '' });
    // The old highlight indexed the ranked subset; reset so Enter doesn't open the wrong
    // category now that the full list is shown again.
    setRootHighlight(NO_HIGHLIGHT);
    inputRef.current?.focus();
  }, []);

  const handleFreeTextChange = useCallback(
    (value: string) => {
      // "@Customer: acme" (or "Customer:") jumps straight into a filter's value picker.
      const command = parseFilterCommand(value, categoriesWithLabels);

      if (command) {
        openDrill(command.filter, command.remainder);

        return;
      }

      setFreeText({ value, source: 'input' });
      setRootHighlight(NO_HIGHLIGHT);
      setIsOpen(true);
    },
    [categoriesWithLabels, openDrill],
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (selectedFilter) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setRootHighlight((index) =>
          Math.min(visibleCategories.length - 1, index < 0 ? 0 : index + 1),
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setRootHighlight((index) => Math.max(0, index - 1));
      } else if (event.key === 'Enter') {
        const highlighted = visibleCategories[rootHighlight];

        if (highlighted) {
          event.preventDefault();
          openDrill(highlighted.filter, '');

          return;
        }

        const search = freeText.value;
        // Mirror the debounced effect's commit gate exactly: a deep-link-sourced
        // value is display-only and must never reach the store, and we apply only
        // when the term meets the minimum length OR the input has been fully cleared.
        // Without the cleared-input case, pressing Enter after emptying the box would
        // only cancel the pending debounced clear and leave the previously committed
        // term applied — the results/highlights would keep the stale term while the
        // field looks empty.
        const searchIsDeepLinkDisplayOnly = freeText.source === 'deep-link';
        const searchMeetsMinLength = search.trim().length >= OMNI_MIN_SEARCH_LENGTH;
        const searchWasCleared = search.length === 0;

        event.preventDefault();
        applyFreeText.cancel();

        if (!searchIsDeepLinkDisplayOnly && (searchMeetsMinLength || searchWasCleared)) {
          ordersDispatchStore.setFilters({ search }, true);
        }

        setIsOpen(false);
        inputRef.current?.blur();
      } else if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [
      selectedFilter,
      visibleCategories,
      rootHighlight,
      openDrill,
      freeText.value,
      freeText.source,
      applyFreeText,
    ],
  );

  const handleClickAway = useCallback(() => {
    setIsOpen(false);
    setSelectedFilter(undefined);
  }, []);

  // Selections apply live; "done" (Enter in the value list) just dismisses the popover.
  const handleRequestClose = useCallback(() => {
    setIsOpen(false);
    inputRef.current?.blur();
  }, []);

  // Free-text: apply debounced, honouring the min-length gate (mirrors SearchBar).
  // A deep-link-sourced value is display-only and must not reach the store.
  useEffect(() => {
    const searchDidChange = ordersDispatchStore.filters.search !== freeText.value;
    // Gate on the trimmed length so a space-padded term behaves the same here as in
    // the Enter-to-commit handler above (which uses search.trim()); a fully empty
    // input still applies so the filter clears.
    const meetsMinLength =
      freeText.value.trim().length >= OMNI_MIN_SEARCH_LENGTH ||
      freeText.value.length === 0;

    if (freeText.source !== 'deep-link' && searchDidChange && meetsMinLength) {
      applyFreeText(freeText.value);
    }

    return () => {
      applyFreeText.cancel();
    };
  }, [
    freeText.value,
    freeText.source,
    applyFreeText,
    ordersDispatchStore.filters.search,
  ]);

  // Re-sync the input when the search filter is cleared elsewhere (e.g. Clear All).
  const previousSearch = usePrevious(ordersDispatchStore.filters.search);
  const searchWasCleared = previousSearch && !ordersDispatchStore.filters.search;

  useEffect(() => {
    if (searchWasCleared) {
      setFreeText({ value: '', source: 'input' });
    }
  }, [searchWasCleared]);

  // Reflect a deep-linked order's friendly id for display only — the 'deep-link' source
  // stops the apply effect above from pushing it to the store as a search.
  useEffect(() => {
    if (ordersDispatchStore.deepLinkedOrder?.orderId) {
      setFreeText({
        value: ordersDispatchStore.deepLinkedOrder.orderId,
        source: 'deep-link',
      });
    }
  }, [ordersDispatchStore.deepLinkedOrder?.orderId]);

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    if (!isDummyQuery && selectedFilter && activeQuery?.data) {
      const noneOptionLabel = getNoneOptionLabel(selectedFilter);

      updateAvailableOptionsAndFilterChips(
        selectedFilter,
        activeQuery.data.concat(
          noneOptionLabel ? [{ label: noneOptionLabel, value: 'none' }] : [],
        ),
      );
    }
  }, [
    updateAvailableOptionsAndFilterChips,
    selectedFilter,
    activeQuery?.data,
    isDummyQuery,
  ]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
      setSearchQuery((state) => ({ ...state, input: '' }));
      setRootHighlight(NO_HIGHLIGHT);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      collaboratorsQuery.clear();
      customersQuery.clear();
      departmentsQuery.clear();
      dispatchNumbersQuery.clear();
      driversQuery.clear();
      dropOffSitesQuery.clear();
      equipmentOwnersQuery.clear();
      equipmentQuery.clear();
      equipmentTypesQuery.clear();
      foremenQuery.clear();
      materialsQuery.clear();
      pickUpSitesQuery.clear();
      projectExternalIdsQuery.clear();
      projectsQuery.clear();
      requestersQuery.clear();
      salespeopleQuery.clear();
      serviceClassesQuery.clear();
      vendorsQuery.clear();
    }
  }, [
    selectedFilter,
    collaboratorsQuery.clear,
    customersQuery.clear,
    departmentsQuery.clear,
    dispatchNumbersQuery.clear,
    driversQuery.clear,
    dropOffSitesQuery.clear,
    equipmentOwnersQuery.clear,
    equipmentQuery.clear,
    equipmentTypesQuery.clear,
    foremenQuery.clear,
    materialsQuery.clear,
    pickUpSitesQuery.clear,
    projectExternalIdsQuery.clear,
    projectsQuery.clear,
    requestersQuery.clear,
    salespeopleQuery.clear,
    serviceClassesQuery.clear,
    vendorsQuery.clear,
  ]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ display: 'flex', flex: 1, maxWidth: OMNI_BAR_MAX_WIDTH_IN_PX }}>
        <Box sx={{ flex: 1, minWidth: OMNI_BAR_MIN_WIDTH_IN_PX, position: 'relative' }}>
          <OutlinedInput
            inputRef={inputRef}
            fullWidth
            onChange={(event) => handleFreeTextChange(event.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleInputKeyDown}
            placeholder={t('dispatch.dispatch_v2.filters.omni_placeholder')}
            size="small"
            value={freeText.value}
            data-ui="page-dispatch__omni-search-bar__input"
            startAdornment={
              <InputAdornment position="start">
                <Search
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: '16px',
                  }}
                />
              </InputAdornment>
            }
            sx={{
              '&.MuiInputBase-root': {
                bgcolor: theme.semantic.surfaceRaised,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.brandV2.borderRadius,
                height: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px - 2px)`,
                px: 1,
                py: 0.5,
                '.MuiInputBase-input': { fontSize: '12px', p: 0 },
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
              },
            }}
          />

          {isOpen && (
            <Paper
              elevation={0}
              sx={{
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.brandV2.borderRadius,
                boxShadow: '0px 1px 10px 0px rgba(60, 64, 67, 0.15)',
                left: 0,
                maxHeight: OMNI_POPOVER_MAX_HEIGHT_IN_PX,
                mt: '3px',
                overflow: 'auto',
                position: 'absolute',
                py: 1,
                right: 0,
                top: '100%',
                zIndex: theme.zIndex.modal,
              }}
              data-ui="page-dispatch__omni-search-bar__popover"
            >
              {selectedFilter ? (
                <>
                  <MenuItem
                    onClick={handleBackToCategories}
                    data-ui="page-dispatch__omni-search-bar__back"
                  >
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      <ChevronLeft sx={{ fontSize: '16px' }} />
                      <Typography
                        color={theme.brandV2.colors.treadBlack}
                        variant="subtitle2"
                      >
                        {categoryLabel(selectedFilter)}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider sx={{ '&.MuiDivider-root': { mt: 0, mb: 1, mx: 2 } }} />

                  <FilterMenuItem
                    focusSearchFieldOnMount
                    searchValue={searchQuery.input}
                    onSearchValueChange={searchValueChangeCallback}
                    onRequestBack={handleBackToCategories}
                    onRequestClose={handleRequestClose}
                    loadingReason={activeQuery?.loadingReason}
                    selectAllOptionLabel={
                      (activeQuery?.data?.length ?? 0) <= 1
                        ? undefined
                        : allEntitiesLabel(selectedFilter)
                    }
                    options={activeQuery?.data ?? []}
                    selectedOptions={selectedOptions}
                    onSelectedOptionsChange={handleSelectedOptionsChange}
                    onFetchMore={fetchMoreCallback}
                    selectNoneOptionLabel={getNoneOptionLabel(selectedFilter)}
                  />
                </>
              ) : (
                <>
                  {freeText.value.trim().length >= OMNI_MIN_SEARCH_LENGTH && (
                    <Typography
                      color={theme.brandV2.colors.treadGray6}
                      variant="caption"
                      sx={{ display: 'block', px: 2, py: 0.5 }}
                    >
                      {t('dispatch.dispatch_v2.filters.search_orders_for', {
                        query: freeText.value.trim(),
                      })}
                    </Typography>
                  )}

                  {visibleCategories.length === 0 ? (
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography color={theme.brandV2.colors.treadGray3} variant="body2">
                        {t('dispatch.dispatch_v2.filters.no_matching_filters')}
                      </Typography>
                    </Box>
                  ) : (
                    rootSections.map((section) =>
                      section.categories.length === 0 ? null : (
                        <Box key={section.key}>
                          {section.label && (
                            <Typography
                              color={theme.brandV2.colors.treadGray6}
                              variant="caption"
                              sx={{ display: 'block', px: 2, py: 0.5 }}
                            >
                              {section.label}
                            </Typography>
                          )}

                          {section.categories.map((category) => {
                            const index =
                              highlightIndexByFilter.get(category.filter) ?? NO_HIGHLIGHT;

                            return (
                              <MenuItem
                                key={category.filter}
                                selected={index === rootHighlight}
                                onClick={() => openDrill(category.filter, '')}
                                onMouseEnter={() => setRootHighlight(index)}
                                data-ui="page-dispatch__omni-search-bar__category"
                              >
                                <Box
                                  sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                  }}
                                >
                                  <Typography
                                    color={theme.brandV2.colors.treadBlack}
                                    variant="subtitle2"
                                  >
                                    {category.label}
                                  </Typography>

                                  <Box
                                    sx={{
                                      alignItems: 'center',
                                      display: 'flex',
                                      gap: 1,
                                    }}
                                  >
                                    {activeFilters.has(category.filter) && (
                                      <Check
                                        sx={{
                                          color: theme.brandV2.colors.treadOrange,
                                          fontSize: '16px',
                                        }}
                                      />
                                    )}
                                    <ChevronRight sx={{ fontSize: '16px' }} />
                                  </Box>
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Box>
                      ),
                    )
                  )}
                </>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
});

export default OmniSearchBar;
